:host {
  --_height: var(--select-input-container-height-md);
  --_padding-inline: var(--select-input-container-padding-inline-md);
  --_gap: var(--select-input-container-gap-md);
  --_radius: var(--select-input-container-border-radius);
  --_border-width-default: var(--select-input-container-border-width-default);
  --_border-width-emphasized: var(--select-input-container-border-width-emphasized);
  --_focus-ring-width: var(--select-input-container-focus-ring-width);
  --_transition-duration: var(--select-input-container-transition-duration);
  --_transition-easing: var(--select-input-container-transition-timing-function);

  --_control-font-size: var(--select-input-control-font-size-md);
  --_control-line-height: var(--select-input-control-line-height-md);
  --_icon-size: var(--select-input-icon-size-md);
  --_chevron-size: var(--select-input-chevron-size-md);

  --_option-min-block-size: var(--select-input-option-min-block-size-md);
  --_option-font-size: var(--select-input-option-font-size-md);
  --_option-line-height: var(--select-input-option-line-height-md);
  --_option-icon-size: var(--select-input-option-icon-size-md);

  --_bg: var(--select-input-default-background-default);
  --_text: var(--select-input-default-text-default);
  --_text-placeholder: var(--select-input-default-text-placeholder);
  --_border-color: var(--select-input-default-border-default);
  --_border-color-hover: var(--select-input-default-border-hover);
  --_border-color-focus: var(--select-input-default-border-focus);
  --_focus-ring-color: var(--select-input-default-focus-ring);

  display: flex;
  flex-direction: column;
  gap: var(--select-input-field-gap);
  inline-size: 100%;
  font-family: var(--select-input-control-font-family);
  color: var(--_text);
}

:host([size='lg']) {
  --_height: var(--select-input-container-height-lg);
  --_padding-inline: var(--select-input-container-padding-inline-lg);
  --_gap: var(--select-input-container-gap-lg);
  --_control-font-size: var(--select-input-control-font-size-lg);
  --_control-line-height: var(--select-input-control-line-height-lg);
  --_icon-size: var(--select-input-icon-size-lg);
  --_chevron-size: var(--select-input-chevron-size-lg);
  --_option-min-block-size: var(--select-input-option-min-block-size-lg);
  --_option-font-size: var(--select-input-option-font-size-lg);
  --_option-line-height: var(--select-input-option-line-height-lg);
  --_option-icon-size: var(--select-input-option-icon-size-lg);
}

:host(.variant-destructive) {
  --_border-color: var(--select-input-destructive-border-default);
  --_border-color-hover: var(--select-input-destructive-border-hover);
  --_border-color-focus: var(--select-input-destructive-border-focus);
  --_focus-ring-color: var(--select-input-destructive-focus-ring);
}

/* ---------- Label ---------- */

.label {
  display: flex;
  align-items: center;
  gap: var(--select-input-label-gap);
  font-family: var(--select-input-label-font-family);
  font-size: var(--select-input-label-font-size);
  line-height: var(--select-input-label-line-height);
  font-weight: var(--select-input-label-font-weight);
  color: var(--select-input-label-color-default);
  inline-size: 100%;
  min-inline-size: 0;
}

.label-text {
  display: block;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.required-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--select-input-label-required-mark-size);
  block-size: var(--select-input-label-required-mark-size);
  color: var(--select-input-label-required-mark-color);
  font-weight: var(--select-input-control-font-weight);
  line-height: 1;
}

:host(.is-disabled) .label {
  color: var(--select-input-label-color-disabled);
}

:host(.is-disabled) .required-mark {
  color: var(--select-input-label-color-disabled);
}

:host(:not(.has-label)) .label {
  display: none;
}

/* ---------- Control row ---------- */

.control-wrapper {
  position: relative;
  inline-size: 100%;
}

.control {
  display: flex;
  align-items: center;
  gap: var(--_gap);
  block-size: var(--_height);
  padding-inline: var(--_padding-inline);
  background-color: var(--_bg);
  border: var(--_border-width-default) solid var(--_border-color);
  border-radius: var(--_radius);
  transition:
    border-color var(--_transition-duration) var(--_transition-easing),
    box-shadow var(--_transition-duration) var(--_transition-easing),
    background-color var(--_transition-duration) var(--_transition-easing);
}

:host(:not(.is-disabled):not(.is-focused):not(.is-open):hover) .control {
  border-color: var(--_border-color-hover);
  box-shadow: inset 0 0 0 calc(var(--_border-width-emphasized) - var(--_border-width-default))
    var(--_border-color-hover);
}

:host(.is-focused:not(.is-disabled)) .control,
:host(.is-open:not(.is-disabled)) .control {
  border-color: var(--_border-color-focus);
  box-shadow:
    inset 0 0 0 calc(var(--_border-width-emphasized) - var(--_border-width-default)) var(--_border-color-focus),
    0 0 0 var(--spacing-4) var(--_focus-ring-color);
}

:host(.is-disabled) .control {
  background-color: var(--select-input-default-background-disabled);
  border-color: var(--select-input-default-border-disabled);
  cursor: not-allowed;
}

/* ---------- Icon slots / chevron ---------- */

.control-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: var(--_icon-size);
  block-size: var(--_icon-size);
  color: var(--select-input-icon-color-default);
}

.control-icon-end .chevron {
  inline-size: var(--_chevron-size);
  block-size: var(--_chevron-size);
  color: var(--select-input-chevron-color-default);
  transition: transform var(--select-input-chevron-transition-duration)
    var(--select-input-chevron-transition-timing-function);
}

:host(.is-open) .control-icon-end .chevron {
  transform: rotate(180deg);
}

:host(.is-disabled) .control-icon,
:host(.is-disabled) .control-icon-end .chevron {
  color: var(--select-input-chevron-color-disabled);
}

:host(:not(.has-icon-start)) .control-icon-start {
  display: none;
}

::slotted([slot='icon-start']) {
  color: inherit;
}

/* ---------- Trigger button ---------- */

.trigger {
  flex: 1 1 auto;
  min-inline-size: 0;
  inline-size: 100%;
  block-size: 100%;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: var(--_control-font-size);
  line-height: var(--_control-line-height);
  font-weight: var(--select-input-control-font-weight);
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  text-align: start;
  display: flex;
  align-items: center;
}

.trigger:disabled {
  cursor: not-allowed;
  color: var(--select-input-default-text-disabled);
  -webkit-text-fill-color: var(--select-input-default-text-disabled);
}

:host(.is-readonly:not(.is-disabled)) .trigger {
  cursor: default;
}

.trigger:focus,
.trigger:focus-visible {
  outline: none;
}

.trigger-text {
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trigger-text.is-placeholder {
  color: var(--_text-placeholder);
}

/* ---------- Listbox popover ---------- */

.listbox {
  position: absolute;
  inset-inline-start: 0;
  inline-size: 100%;
  inset-block-start: calc(100% + var(--select-input-listbox-margin-block-start));
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: var(--select-input-listbox-gap);
  padding: var(--select-input-listbox-padding);
  background-color: var(--select-input-listbox-background);
  border: var(--select-input-listbox-border-width) solid var(--select-input-listbox-border-color);
  border-radius: var(--select-input-listbox-border-radius);
  box-shadow: var(--select-input-listbox-shadow);
  max-block-size: var(--select-input-listbox-max-block-size);
  overflow-y: auto;
}

.listbox[hidden] {
  display: none;
}

.listbox-empty {
  padding: var(--spacing-8) var(--spacing-12);
  font-size: var(--_option-font-size);
  line-height: var(--_option-line-height);
  color: var(--_text-placeholder);
}

/* ---------- Listbox option ---------- */

.option {
  display: flex;
  align-items: center;
  gap: var(--select-input-option-gap);
  min-block-size: var(--_option-min-block-size);
  padding-block: var(--select-input-option-padding-block);
  padding-inline: var(--select-input-option-padding-inline);
  border-radius: var(--select-input-option-border-radius);
  color: var(--select-input-option-text-default);
  font-family: var(--select-input-option-font-family);
  font-size: var(--_option-font-size);
  line-height: var(--_option-line-height);
  font-weight: var(--select-input-option-font-weight-default);
  cursor: pointer;
  user-select: none;
  transition:
    background-color var(--_transition-duration) var(--_transition-easing),
    color var(--_transition-duration) var(--_transition-easing);
}

.option.is-highlighted {
  background-color: var(--select-input-option-background-hover);
}

.option.is-selected {
  background-color: var(--select-input-option-background-selected);
  color: var(--select-input-option-text-selected);
  font-weight: var(--select-input-option-font-weight-selected);
}

.option.is-selected.is-highlighted {
  background-color: var(--select-input-option-background-active);
}

.option.is-disabled {
  color: var(--select-input-option-text-disabled);
  cursor: not-allowed;
  background-color: transparent;
}

.option-label {
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-check {
  inline-size: var(--_option-icon-size);
  block-size: var(--_option-icon-size);
  flex-shrink: 0;
  color: var(--select-input-option-check-color);
}

/* ---------- Assistive (helper / error) ---------- */

.assistive {
  display: flex;
  align-items: flex-start;
  gap: var(--select-input-assistive-gap-default);
  font-family: var(--select-input-assistive-font-family);
  font-size: var(--select-input-assistive-font-size);
  line-height: var(--select-input-assistive-line-height);
  font-weight: var(--select-input-assistive-font-weight);
  color: var(--select-input-assistive-color-default);
  inline-size: 100%;
  min-inline-size: 0;
}

.assistive-error {
  gap: var(--select-input-assistive-gap-error);
  color: var(--select-input-assistive-color-error);
}

.assistive-icon {
  flex-shrink: 0;
  inline-size: var(--select-input-assistive-icon-size);
  block-size: var(--select-input-assistive-icon-size);
  margin-block-start: 0;
}

.assistive-text {
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  text-overflow: ellipsis;
}

/* ---------- Hidden default slot (option children sink) ---------- */

.default-slot {
  display: none;
}

/* ---------- prefers-reduced-motion ---------- */

@media (prefers-reduced-motion: reduce) {
  .control,
  .option,
  .control-icon-end .chevron {
    transition: none;
  }
}
